<template>
    <div class="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4 sm:px-6 lg:px-8">
        <!-- Enhanced Toast Notification -->
        <div v-if="showToast" 
             :class="[
                 'fixed top-6 right-6 z-50 p-6 rounded-xl shadow-2xl max-w-md transition-all duration-500 transform',
                 toastType === 'success' 
                     ? 'bg-gradient-to-r from-green-500 to-green-600 text-white border-l-4 border-green-400' 
                     : 'bg-gradient-to-r from-red-500 to-red-600 text-white border-l-4 border-red-400'
             ]"
             @click="hideToast">
            <div class="flex items-start">
                <div class="flex-shrink-0">
                    <svg v-if="toastType === 'success'" class="h-6 w-6 text-green-100" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
                    </svg>
                    <svg v-else class="h-6 w-6 text-red-100" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"></path>
                    </svg>
                </div>
                <div class="mr-4 flex-1">
                    <p class="text-sm font-semibold leading-5">{{ toastMessage }}</p>
                </div>
                <button @click="hideToast" class="inline-flex text-white hover:text-gray-200 transition-colors duration-200">
                    <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"></path>
                    </svg>
                </button>
            </div>
        </div>

        <!-- Main Container -->
        <div class="max-w-4xl mx-auto">
            <!-- Header Section -->
            <div dir="rtl" class="text-center mb-8">
                <div class="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full mb-4">
                    <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"></path>
                    </svg>
                </div>
                <h1 class="text-3xl font-bold text-gray-900 mb-2">إنشاء حساب إداري جديد</h1>
                <p class="text-gray-600 text-lg">أدخل بيانات المدير الجديد لإنشاء الحساب</p>
            </div>

            <!-- Form Container -->
            <div class="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                <!-- Progress Bar -->
                <div class="bg-gray-50 px-8 py-4 border-b border-gray-200">
                    <div class="flex items-center justify-between mb-2">
                        <span class="text-sm font-medium text-gray-700">تقدم إكمال النموذج</span>
                        <span class="text-sm font-medium text-blue-600">{{ formProgress }}%</span>
                    </div>
                    <div class="w-full bg-gray-200 rounded-full h-2">
                        <div class="bg-gradient-to-r from-blue-500 to-indigo-600 h-2 rounded-full transition-all duration-300" 
                             :style="{ width: formProgress + '%' }"></div>
                    </div>
                </div>
                
                <form @submit.prevent="createAdmin" ref="addAdmin" class="p-8">
                    <!-- Personal Information Section -->
                    <div class="mb-8">
                        <div class="flex items-center mb-6">
                            <div class="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                                <svg class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                                </svg>
                            </div>
                            <h2 class="text-xl font-semibold text-gray-900">المعلومات الشخصية</h2>
                        </div>
                        
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <!-- Full Name -->
                            <div class="space-y-2">
                                <label for="fullName" class="block text-sm font-medium text-gray-700">
                                    الاسم الكامل <span class="text-red-500">*</span>
                                </label>
                                <div class="relative">
                                    <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                        <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                                        </svg>
                                    </div>
                                    <input 
                                        type="text" 
                                        name="fullName" 
                                        id="fullName" 
                                        v-model="fullName"
                                        placeholder="أدخل الاسم الكامل"
                                        class="block w-full pr-10 pl-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                                        required 
                                        oninvalid="this.setCustomValidity('الرجاء إدخال الاسم الكامل')"
                                        oninput="setCustomValidity('')" 
                                    />
                                </div>
                            </div>

                            <!-- Email -->
                            <div class="space-y-2">
                                <label for="email" class="block text-sm font-medium text-gray-700">
                                    البريد الإلكتروني <span class="text-red-500">*</span>
                                </label>
                                <div class="relative">
                                    <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                        <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                                        </svg>
                                    </div>
                                    <input 
                                        type="email" 
                                        name="email" 
                                        id="email" 
                                        v-model="email" 
                                        placeholder="example@email.com"
                                        class="block w-full pr-10 pl-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                                        required 
                                        oninvalid="this.setCustomValidity('الرجاء إدخال البريد الإلكتروني')"
                                        oninput="setCustomValidity('')" 
                                        @input="validateEmail" 
                                    />
                                </div>
                                <span v-if="emailError" class="text-red-500 text-sm flex items-center">
                                    <svg class="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                        <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd"></path>
                                    </svg>
                                    {{ emailError }}
                                </span>
                            </div>

                            <!-- National ID field removed as it's not essential for app functionality -->

                            <!-- Phone Number -->
                            <div class="space-y-2">
                                <label for="phoneNumber" class="block text-sm font-medium text-gray-700">
                                    رقم الهاتف <span class="text-red-500">*</span>
                                </label>
                                <div class="relative">
                                    <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                        <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
                                        </svg>
                                    </div>
                                    <div class="flex">
                                        <span class="inline-flex items-center px-4 py-3 rounded-l-lg border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm font-medium">
                                            +966
                                        </span>
                                        <input 
                                            type="tel" 
                                            name="phoneNumber" 
                                            id="phoneNumber" 
                                            v-model="phoneNumber"
                                            placeholder="5XXXXXXXX"
                                            class="block w-full pr-10 pl-4 py-3 border border-gray-300 rounded-r-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                                            required 
                                            oninvalid="this.setCustomValidity('الرجاء إدخال رقم الهاتف')"
                                            oninput="setCustomValidity('')" 
                                            maxlength="9" 
                                            minlength="9" 
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Professional Information Section -->
                    <div class="mb-8">
                        <div class="flex items-center mb-6">
                            <div class="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3">
                                <svg class="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H8a2 2 0 01-2-2V8a2 2 0 012-2V6"></path>
                                </svg>
                            </div>
                            <h2 class="text-xl font-semibold text-gray-900">المعلومات المهنية</h2>
                        </div>
                        
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <!-- Qualification -->
                            <div class="space-y-2">
                                <label for="qualification" class="block text-sm font-medium text-gray-700">
                                    المؤهل العلمي <span class="text-red-500">*</span>
                                </label>
                                <div class="relative">
                                    <select 
                                        id="qualification" 
                                        name="qualification" 
                                        v-model="qualificationValue"
                                        :disabled="isLoadingQualifications"
                                        class="block w-full py-3 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 appearance-none bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                                        required
                                    >
                                        <option value="" disabled selected>
                                            {{ isLoadingQualifications ? 'جاري التحميل...' : 'اختر المؤهل العلمي' }}
                                        </option>
                                        <option v-for="master in masterQualificationsList" :key="master.id" :value="master.id">
                                            {{ master.qualificationValue }}
                                        </option>
                                    </select>
                                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <svg v-if="isLoadingQualifications" class="animate-spin h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        <svg v-else class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                                        </svg>
                                    </div>
                                </div>
                            </div>

                            <!-- Region -->
                            <div class="space-y-2">
                                <label for="region" class="block text-sm font-medium text-gray-700">
                                    المنطقة <span class="text-red-500">*</span>
                                </label>
                                <div class="relative">
                                    <select 
                                        id="region" 
                                        name="region" 
                                        v-model="regionId"
                                        :disabled="isLoadingRegions"
                                        class="block w-full py-3 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 appearance-none bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                                        required
                                    >
                                        <option value="" disabled selected>
                                            {{ isLoadingRegions ? 'جاري التحميل...' : 'اختر المنطقة' }}
                                        </option>
                                        <option v-for="master in masterRegionsList" :key="master.id" :value="master.id">
                                            {{ master.regionNameAr }}
                                        </option>
                                    </select>
                                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <svg v-if="isLoadingRegions" class="animate-spin h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        <svg v-else class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                                        </svg>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Security Section -->
                    <div class="mb-8">
                        <div class="flex items-center mb-6">
                            <div class="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center mr-3">
                                <svg class="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
                                </svg>
                            </div>
                            <h2 class="text-xl font-semibold text-gray-900">أمان الحساب</h2>
                        </div>
                        
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <!-- Password -->
                            <div class="space-y-2">
                                <label for="password" class="block text-sm font-medium text-gray-700">
                                    كلمة المرور <span class="text-red-500">*</span>
                                </label>
                                <div class="relative">
                                    <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                        <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
                                        </svg>
                                    </div>
                                    <input 
                                        name="password" 
                                        id="password" 
                                        v-model="password" 
                                        placeholder="أدخل كلمة المرور"
                                        class="block w-full pr-10 pl-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                                        required 
                                        oninvalid="this.setCustomValidity('الرجاء إدخال كلمة المرور')"
                                        oninput="setCustomValidity('')" 
                                        :type="showPassword ? 'text' : 'password'"
                                        @input="validatePassword" 
                                    />
                                    <button 
                                        type="button"
                                        @click="showPassword = !showPassword"
                                        class="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors duration-200"
                                    >
                                        <svg v-if="showPassword" class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"></path>
                                        </svg>
                                        <svg v-else class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                                        </svg>
                                    </button>
                                </div>
                                <span v-if="passwordError" class="text-red-500 text-sm flex items-center">
                                    <svg class="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                        <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd"></path>
                                    </svg>
                                    {{ passwordError }}
                                </span>
                            </div>

                            <!-- Confirm Password -->
                            <div class="space-y-2">
                                <label for="confirmPassword" class="block text-sm font-medium text-gray-700">
                                    تأكيد كلمة المرور <span class="text-red-500">*</span>
                                </label>
                                <div class="relative">
                                    <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                        <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
                                        </svg>
                                    </div>
                                    <input 
                                        name="confirmPassword" 
                                        id="confirmPassword" 
                                        v-model="confirmPassword"
                                        placeholder="أعد إدخال كلمة المرور"
                                        class="block w-full pr-10 pl-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                                        required 
                                        oninvalid="this.setCustomValidity('الرجاء تأكيد كلمة المرور')"
                                        oninput="setCustomValidity('')" 
                                        :type="showPassword ? 'text' : 'password'"
                                        @input="validatePassword" 
                                    />
                                </div>
                                <span v-if="confirmPasswordError" class="text-red-500 text-sm flex items-center">
                                    <svg class="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                        <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd"></path>
                                    </svg>
                                    {{ confirmPasswordError }}
                                </span>
                            </div>
                        </div>

                        <!-- Password Requirements -->
                        <div class="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                            <h4 class="text-sm font-medium text-blue-900 mb-2">متطلبات كلمة المرور:</h4>
                            <ul class="text-sm text-blue-800 space-y-1">
                                <li class="flex items-center">
                                    <svg class="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                        <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                                    </svg>
                                    يجب أن تكون بين 8 و 15 حرفاً
                                </li>
                                <li class="flex items-center">
                                    <svg class="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                        <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                                    </svg>
                                    يجب أن تحتوي على حرف كبير وحرف صغير
                                </li>
                                <li class="flex items-center">
                                    <svg class="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                        <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                                    </svg>
                                    يجب أن تحتوي على رقم واحد على الأقل
                                </li>
                                <li class="flex items-center">
                                    <svg class="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                        <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                                    </svg>
                                    يجب أن تحتوي على رمز خاص مثل @ # !
                                </li>
                            </ul>
                        </div>
                    </div>

                    <!-- Submit Button -->
                    <div class="flex justify-center pt-6">
                        <button 
                            type="submit" 
                            :disabled="passwordSuccess || isSubmitting" 
                            class="inline-flex items-center px-8 py-4 border border-transparent text-lg font-medium rounded-xl text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                        >
                            <svg v-if="isSubmitting" class="animate-spin -mr-2 ml-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <svg v-else class="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                            </svg>
                            {{ isSubmitting ? 'جاري إنشاء الحساب...' : 'إنشاء الحساب الإداري' }}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>



<script>
import url from '@/url';
import axios from 'axios';

class MasterQualificationsDto {
    constructor(data) {
        this.id = data.id || null;
        this.isActive = data.isActive || null;
        this.qualificationCode = data.qualificationCode || null;
        this.qualificationValue = data.qualificationValue || null;
    }
}

class MasterRegionsDto {
    constructor(data) {
        this.id = data.id || null;
        this.countryCode = data.countryCode || null;
        this.regionCode = data.regionCode || null;
        this.regionNameAr = data.regionNameAr || null;
        this.regionNameEn = data.regionNameEn || null;
    }
}

export default {
    data() {
        return {
            fullName: "",
            email: "",
            // idNumber: "", // Removed as it's not essential for app functionality
            phoneNumber: "",
            password: "",
            confirmPassword: "",
            qualificationValue: '',
            regionId: '',
            showPassword: false,
            emailError: "",
            passwordError: '',
            passwordSuccess: true,
            confirmPasswordError: '',
            masterQualificationsList: [],
            masterRegionsList: [],
            // Toast notification properties
            showToast: false,
            toastMessage: '',
            toastType: 'success', // 'success' or 'error'
            isSubmitting: false,
            // Loading states
            isLoadingQualifications: false,
            isLoadingRegions: false,
            // Form progress tracking (removed - now computed)
        }
    }, 
    computed: {
        // Calculate form completion progress
        formProgress() {
            let completed = 0;
            const total = 7; // Total number of required fields (removed national ID)
            
            if (this.fullName.trim()) completed++;
            if (this.email.trim() && !this.emailError) completed++;
            // if (this.idNumber.trim() && /^\d{10}$/.test(this.idNumber.trim())) completed++; // Removed as it's not essential for app functionality
            if (this.phoneNumber.trim() && /^5\d{8}$/.test(this.phoneNumber.trim())) completed++;
            if (this.qualificationValue) completed++;
            if (this.regionId) completed++;
            if (this.password && !this.passwordError) completed++;
            if (this.confirmPassword && this.password === this.confirmPassword) completed++;
            
            return Math.round((completed / total) * 100);
        }
    },
    mounted() {
        this.masterQualifications();
        this.masterRegions();
    }, 
    methods: {
        validateEmail() {
            // Enhanced email validation with better regex
            const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
            if (!this.email.trim()) {
                this.emailError = "";
            } else if (!emailRegex.test(this.email)) {
                this.emailError = "الرجاء إدخال بريد إلكتروني صحيح";
            } else {
                this.emailError = "";
            }
        },
        async createAdmin() {
            // Enhanced form validation
            if (!this.validateForm()) {
                return;
            }
            
            // Additional password confirmation check
            if (this.password !== this.confirmPassword) {
                this.showErrorMessage('كلمة المرور غير متطابقة');
                return;
            }
            
            this.isSubmitting = true;
            
            try {
                // Prepare request data with proper validation
                const requestData = {
                    fullName: this.fullName.trim(),
                    email: this.email.trim().toLowerCase(),
                    password: this.password,
                    // nationalId: this.idNumber.trim(), // Removed as it's not essential for app functionality
                    phone: this.phoneNumber.trim(),
                    qualificationId: this.qualificationValue,
                    regionId: this.regionId,
                };

                // Validate phone number format
                if (!/^5\d{8}$/.test(this.phoneNumber)) {
                    throw new Error('رقم الهاتف يجب أن يبدأ بـ 5 ويتكون من 9 أرقام');
                }

                // National ID validation removed as it's not essential for app functionality

                const response = await axios.post(
                    process.env.VUE_APP_SERVICE_URL + url.create_admin,
                    requestData,
                    {
                        headers: {
                            'X-Authorization': localStorage.getItem('token'),
                            'Content-Type': 'application/json',
                        },
                        timeout: 30000, // 30 second timeout
                    }
                );

                if (response?.data) {
                    console.log('Admin created successfully:', response.data);
                    
                    // Reset form
                    this.resetForm();
                    
                    // Show success message
                    const successMessage = response.data.result?.mesAr || 
                                         response.data.message || 
                                         'تم إنشاء الحساب الإداري بنجاح';
                    this.showSuccessMessage(successMessage);
                }
                
            } catch (error) {
                console.error('Admin creation error:', error);
                
                let errorMessage = 'حدث خطأ ما الرجاء المحاولة مرة اخرى';
                
                // Handle different types of errors
                if (error.response?.data) {
                    const errorData = error.response.data;
                    
                    // Handle structured error responses
                    if (errorData.mesAr) {
                        errorMessage = errorData.mesAr;
                    } else if (errorData.messageAr) {
                        errorMessage = errorData.messageAr;
                    } else if (errorData.result?.mesAr) {
                        errorMessage = errorData.result.mesAr;
                    } else if (errorData.result?.data) {
                        if (Array.isArray(errorData.result.data)) {
                            errorMessage = errorData.result.data.join('\n');
                        } else if (typeof errorData.result.data === 'string') {
                            errorMessage = errorData.result.data;
                        }
                    } else if (errorData.message) {
                        errorMessage = errorData.message;
                    }
                } else if (error.message) {
                    // Handle network or validation errors
                    if (error.message.includes('timeout')) {
                        errorMessage = 'انتهت مهلة الاتصال، الرجاء المحاولة مرة أخرى';
                    } else if (error.message.includes('Network Error')) {
                        errorMessage = 'خطأ في الاتصال بالخادم، الرجاء التحقق من اتصال الإنترنت';
                    } else {
                        errorMessage = error.message;
                    }
                }
                
                this.showErrorMessage(errorMessage);
                
            } finally {
                this.isSubmitting = false;
            }
        },
        async masterQualifications() {
            this.isLoadingQualifications = true;
            try {
                // Get tenant_id from JWT token
                let tenantId = null;
                try {
                    const token = localStorage.getItem('token');
                    if (token) {
                        const payload = JSON.parse(atob(token.split('.')[1]));
                        tenantId = payload.tenantId || payload.tenant_id || null;
                    }
                } catch (e) {
                    console.warn('Could not extract tenantId from token:', e);
                }

                // Build URL with tenantId as query parameter
                let urlWithParams = url.master_qualifications;
                if (tenantId) {
                    urlWithParams += `?tenantId=${encodeURIComponent(tenantId)}`;
                }

                const response = await axios.get(process.env.VUE_APP_SERVICE_URL + urlWithParams, {
                    timeout: 10000
                });
                
                if (response?.data?.result?.data) {
                    this.masterQualificationsList = response.data.result.data.map(data => new MasterQualificationsDto(data));
                    console.log('Qualifications loaded:', this.masterQualificationsList.length);
                } else {
                    throw new Error('No qualifications data received');
                }
            } catch (error) {
                console.error('Error fetching qualifications:', error);
                this.showErrorMessage('فشل في تحميل قائمة المؤهلات العلمية');
            } finally {
                this.isLoadingQualifications = false;
            }
        },
        
        async masterRegions() {
            this.isLoadingRegions = true;
            try {
                const response = await axios.get(process.env.VUE_APP_SERVICE_URL + url.master_regions, {
                    headers: {
                        'X-Authorization': localStorage.getItem('token'),
                    },
                    timeout: 10000
                });
                
                if (response?.data?.result?.data) {
                    this.masterRegionsList = response.data.result.data.map(data => new MasterRegionsDto(data));
                    console.log('Regions loaded:', this.masterRegionsList.length);
                } else {
                    throw new Error('No regions data received');
                }
            } catch (error) {
                console.error('Error fetching regions:', error);
                this.showErrorMessage('فشل في تحميل قائمة المناطق');
            } finally {
                this.isLoadingRegions = false;
            }
        },
        validatePassword() {
            // Reset error messages
            this.passwordError = '';
            this.confirmPasswordError = '';

            // Enhanced password validation
            const minLength = 8;
            const maxLength = 15;
            const specialCharRegex = /[@#!$%^&*()_+\-=[\]{};':"\\|,.<>/?]/;
            const uppercaseRegex = /[A-Z]/;
            const lowercaseRegex = /[a-z]/;
            const numberRegex = /[0-9]/;

            // Check password length
            if (this.password.length < minLength || this.password.length > maxLength) {
                this.passwordError = `كلمة المرور يجب أن تكون بين ${minLength} و ${maxLength} حرفاً`;
                this.passwordSuccess = true;
                return;
            }

            // Check for required character types
            const hasSpecialChar = specialCharRegex.test(this.password);
            const hasUppercase = uppercaseRegex.test(this.password);
            const hasLowercase = lowercaseRegex.test(this.password);
            const hasNumber = numberRegex.test(this.password);

            if (!hasSpecialChar || !hasUppercase || !hasLowercase || !hasNumber) {
                this.passwordError = 'كلمة المرور يجب أن تحتوي على حرف كبير، حرف صغير، رقم، ورمز خاص';
                this.passwordSuccess = true;
                return;
            }

            // Check password confirmation
            if (this.confirmPassword && this.password !== this.confirmPassword) {
                this.confirmPasswordError = 'كلمة المرور غير متطابقة';
                this.passwordSuccess = true;
                return;
            }

            // All validations passed
            this.passwordSuccess = false;
        },
        
        showSuccessMessage(message) {
            this.toastMessage = message;
            this.toastType = 'success';
            this.showToast = true;
            
            // Auto-hide after 5 seconds
            setTimeout(() => {
                this.hideToast();
            }, 5000);
        },
        
        showErrorMessage(message) {
            this.toastMessage = message;
            this.toastType = 'error';
            this.showToast = true;
            
            // Auto-hide after 8 seconds for errors
            setTimeout(() => {
                this.hideToast();
            }, 8000);
        },
        
        hideToast() {
            this.showToast = false;
        },
        
        // Enhanced validation method
        validateForm() {
            let isValid = true;
            let errorMessages = [];
            
            // Check required fields with enhanced validation
            if (!this.fullName.trim()) {
                errorMessages.push('الرجاء إدخال الاسم الكامل');
                isValid = false;
            } else if (this.fullName.trim().length < 3) {
                errorMessages.push('الاسم الكامل يجب أن يكون 3 أحرف على الأقل');
                isValid = false;
            }
            
            if (!this.email.trim()) {
                errorMessages.push('الرجاء إدخال البريد الإلكتروني');
                isValid = false;
            } else {
                const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
                if (!emailRegex.test(this.email.trim())) {
                    errorMessages.push('الرجاء إدخال بريد إلكتروني صحيح');
                    isValid = false;
                }
            }
            
            // National ID validation removed as it's not essential for app functionality
            
            if (!this.phoneNumber.trim()) {
                errorMessages.push('الرجاء إدخال رقم الهاتف');
                isValid = false;
            } else if (!/^5\d{8}$/.test(this.phoneNumber.trim())) {
                errorMessages.push('رقم الهاتف يجب أن يبدأ بـ 5 ويتكون من 9 أرقام');
                isValid = false;
            }
            
            if (!this.password) {
                errorMessages.push('الرجاء إدخال كلمة المرور');
                isValid = false;
            } else if (this.password.length < 8 || this.password.length > 15) {
                errorMessages.push('كلمة المرور يجب أن تكون بين 8 و 15 حرفاً');
                isValid = false;
            }
            
            if (!this.confirmPassword) {
                errorMessages.push('الرجاء تأكيد كلمة المرور');
                isValid = false;
            } else if (this.password !== this.confirmPassword) {
                errorMessages.push('كلمة المرور غير متطابقة');
                isValid = false;
            }
            
            if (!this.qualificationValue) {
                errorMessages.push('الرجاء اختيار المؤهل العلمي');
                isValid = false;
            }
            
            if (!this.regionId) {
                errorMessages.push('الرجاء اختيار المنطقة');
                isValid = false;
            }
            
            if (!isValid) {
                this.showErrorMessage(errorMessages.join('\n'));
            }
            
            return isValid;
        },
        
        // Reset form method
        resetForm() {
            this.fullName = '';
            this.email = '';
            // this.idNumber = ''; // Removed as it's not essential for app functionality
            this.phoneNumber = '';
            this.password = '';
            this.confirmPassword = '';
            this.qualificationValue = '';
            this.regionId = '';
            this.showPassword = false;
            this.emailError = '';
            this.passwordError = '';
            this.confirmPasswordError = '';
            this.passwordSuccess = true;
            
            // Reset form validation state
            if (this.$refs.addAdmin) {
                this.$refs.addAdmin.reset();
            }
        },
    }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->

<style scoped>
/* Custom animations and enhancements */
@keyframes fadeInUp {
    from {
        opacity: 0;
        transform: translateY(20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.form-section {
    animation: fadeInUp 0.6s ease-out;
}

/* Enhanced focus states */
input:focus, select:focus {
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Custom scrollbar for select dropdowns */
select::-webkit-scrollbar {
    width: 8px;
}

select::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 4px;
}

select::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
}

select::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
}

/* Toast animation */
.toast-enter-active, .toast-leave-active {
    transition: all 0.3s ease;
}

.toast-enter-from, .toast-leave-to {
    opacity: 0;
    transform: translateX(100%);
}

/* Progress bar animation */
.progress-bar {
    transition: width 0.5s ease-in-out;
}

/* Button hover effects */
.btn-primary {
    position: relative;
    overflow: hidden;
}

.btn-primary::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
    transition: left 0.5s;
}

.btn-primary:hover::before {
    left: 100%;
}

/* Form validation styles */
.input-error {
    border-color: #ef4444;
    box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
}

.input-success {
    border-color: #10b981;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
}

/* Loading spinner */
.spinner {
    animation: spin 1s linear infinite;
}

@keyframes spin {
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
}

/* Responsive improvements */
@media (max-width: 768px) {
    .form-container {
        padding: 1rem;
    }
    
    .header-section {
        padding: 1rem 0;
    }
    
    .header-section h1 {
        font-size: 1.5rem;
    }
}
</style>